<template>

	<div id="section-total" class="section is-white-ter" :class="{ 'is-collapsed': isCollapsed, 'has-shadow': hasShadow }">
		<div class="container">
			<div class="columns is-vcentered">
				<div class="column">
					<h3 class="subtitle is-4 m-0">Price per month</h3>
					<p v-if="!requiresCustomQuote" class="is-size-1 has-text-navy has-text-weight-bold">
						<number :to="total" :duration="0.6" :format="formatTotal" easing="Expo.easeOut"></number>
					</p>
					<p v-else class="is-size-1 has-text-navy has-text-weight-bold">Contact us</p>
				</div>
				<div class="column has-text-right">
					<send-quote v-if="!requiresCustomQuote"></send-quote>

					<a v-else href="mailto:sales@gearset.com" class="button is-medium is-blue is-outlined">Request custom quote</a>
				</div>
			</div>
		</div>
	</div>

</template>

<script>

import SendQuote from './SendQuote.vue';

export default {
	name: 'total',

	data() {
		return {
			/* Whether the scroll position is past the stuck position of the total bar */
			isScrolledPast: false,
		};
	},

	computed: {
		data() {
			return this.$store.data || {};
		},

		selection() {
			return this.$store.selection || {};
		},

		isCollapsed() {
			return !this.isScrolledPast && this.total === 0 && !this.requiresCustomQuote;
		},

		hasShadow() {
			return !this.isScrolledPast;
		},

		/* Final total monthly spend in cents */
		total() {
			let total = 0;

			/* Compare & Deploy */
			if (this.selection.compare.users > 0 && this.selection.compare.tier !== '') {
				/* Calculate price for number of users for the selected tier */
				total = total + (this.data.compare.products[this.selection.compare.tier].price * this.selection.compare.users);
			}

			/* Automation */
			if (this.selection.compare.users > 0 && this.selection.automation.tier !== '') {
				/* Get the price for the tier (not per-user based) */
				total = total + (this.data.automation.products[this.selection.automation.tier].price);
			}

			/* Save individual pricing for later */
			const compareAndAutomationPrice = total;
			let backupPrice = 0;

			/* Backup */
			if (this.selection.backup.users > 0 && this.selection.backup.tier !== '') {
				/* Calculate price for number of users, and make sure it's at least the minimum contract size */
				const backupTierData = this.data.backup.products[this.selection.backup.tier];
				const backupPriceByUsers = backupTierData.price * this.selection.backup.users;
				backupPrice = this.enforceMinimum(backupPriceByUsers, backupTierData);

				total = total + backupPrice;
			}

			/* Addons */
			Object.keys(this.selection.addons).forEach(addon => {
				if (this.selection.addons[addon]) {
					/* Use either compare+automation, or backup, as the basis for percentage pricing */
					const basePrice = compareAndAutomationPrice > 0 ? compareAndAutomationPrice : backupPrice;

					/* Calculate addon price based on base price, and make sure it's at least the minimum price */
					const addonData = this.data.addons.products[addon];
					const addonPercentagePrice = basePrice * addonData.priceMultiplier;

					total = total + this.enforceMinimum(addonPercentagePrice, addonData);
				}
			});

			/* Code reviews */
			if (this.selection.codeReviews.users > 0 && this.selection.codeReviews.tier !== '') {
				/* Calculate price for number of users for the selected tier */
				total = total + (this.data.codeReviews.products[this.selection.codeReviews.tier].price * this.selection.codeReviews.users);
			}

			return total;
		},

		/* Whether the user needs a custom quote */
		requiresCustomQuote() {
			/* All products, except addons */
			const products = Object.keys(this.data);
			products.splice(products.indexOf('addons'), 1);

			/* Get products whose tier selections indicate custom quote */
			const quotedProducts = products.filter(product => {
				return this.selection[product].tier !== '' && this.data[product].products[this.selection[product].tier].requiresCustomQuote;
			});

			/* All selected addons that require a custom quote */
			const quotedAddons = Object.keys(this.selection.addons).filter(addon => {
				return this.selection.addons[addon] && this.data.addons.products[addon].requiresCustomQuote;
			});

			/* If any tier or any selected addon requires a quote, enforce quote */
			return quotedProducts.length > 0 || quotedAddons.length > 0;
		},
	},

	methods: {
		/* Enforce price minimum, if it exists */
		enforceMinimum(price, data) {
			if ('priceMinimum' in data) {
				return price < data.priceMinimum ? data.priceMinimum : price;
			} else {
				return price;
			}
		},

		/* Format the total for the UI */
		formatTotal(number) {
			return this.formatMoney(number, true);
		},
	},

	mounted() {
		window.addEventListener('scroll', () => {
			/* Determine if this section would be visible */
			const packageElement = document.querySelector('#section-package')?.getBoundingClientRect();
			const totalElement = document.querySelector('#section-total')?.getBoundingClientRect();
			this.isScrolledPast = packageElement.top < window.innerHeight - packageElement.height - totalElement.height;
		});
		window.dispatchEvent(new Event('scroll'));
	},

	watch: {
		/* When the quote total changes */
		total(newTotal) {
			this.$set(this.$store.selection, 'total', newTotal);
		}
	},

	components: {
		SendQuote,
	},
};

</script>

<style lang="scss" scoped>
@import '@gearset/web-core/styles/includes/variables';

.section {
	/* stylelint-disable-next-line value-no-vendor-prefix */
	position: -webkit-sticky;
	position: sticky;
	bottom: 0;
	padding-top: 2rem;
	padding-bottom: 2rem;
	transition: .4s transform cubic-bezier(.33, 1, .68, 1), .2s box-shadow ease-in-out;

	&.is-collapsed {
		transform: translateY(120%);
		transition-timing-function: cubic-bezier(.32, 0, .67, 0), ease-in-out;
	}

	&.has-shadow {
		box-shadow: $box-shadow-strong;
	}

	@include touch {
		padding: 1.5rem;
	}
}

.columns {
	align-items: stretch;

	.column {
		height: auto;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: flex-start;

		&.has-text-right {
			align-items: flex-end;

			@include mobile {
				text-align: left;
				align-items: flex-start;
			}
		}
	}
}

p.is-size-1 {
	font-variant-numeric: tabular-nums;
	letter-spacing: -.02em;

	@include tablet-only {
		font-size: $size-2 !important;
	}
}

@include touch {
	.columns {
		margin-top: -1rem;
		margin-bottom: -1rem;
	}

	.column {
		padding-top: 1rem;
		padding-bottom: 1rem;
	}

	.subtitle.is-4,
	.title.is-5 {
		font-size: $size-7;
	}
}

@include mobile {
	.column {
		&:first-child {
			padding-bottom: 0;
		}
	}
}

</style>
